import type { ApiErrorBody } from '@dishly/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields?: Record<string, string>;

  constructor(status: number, code: string, message: string, fields?: Record<string, string>) {
    super(message);
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

/** True when the request never reached the server (offline, DNS, server down). */
export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.status === 0;
}

/** Fired when the server says the session is gone, so the app can sign the user out locally. */
export const apiEvents = new EventTarget();

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

/** Sends `body` as JSON, or as multipart form data when it is a FormData. */
export async function api<T>(path: string, { method, body, signal }: RequestOptions = {}): Promise<T> {
  const json = body !== undefined && !(body instanceof FormData);
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method: method ?? (body === undefined ? 'GET' : 'POST'),
      headers: json ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: json ? JSON.stringify(body) : (body as FormData | undefined),
      credentials: 'same-origin',
      signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', 'Network request failed');
  }
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const info = (data as ApiErrorBody | null)?.error;
    if (response.status === 401) apiEvents.dispatchEvent(new Event('unauthorized'));
    throw new ApiError(response.status, info?.code ?? 'http_error', info?.message ?? response.statusText, info?.fields);
  }
  return data as T;
}
